// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/QuestionCard.tsx (L128-L148 option button, OptionIcon)
import { Check } from 'lucide-react';
import type { ComponentType } from 'react';

import { selectionSurface } from '@/components/ui/selection';
import { cn } from '@/lib/utils';

export interface OptionRowProps {
  label: string;
  /** A muted line under the label (a first-person pain line). */
  hint?: string;
  /** A lucide icon, or a logo component, for the leading tile. */
  icon?: ComponentType<{ className?: string }>;
  selected: boolean;
  /** Greyed with the design system's disabled colours (never opacity). */
  disabled?: boolean;
  onClick: () => void;
  ariaLabel?: string;
}

/**
 * One selectable option of a docked question, TP's markup. A plain button with `aria-pressed`, for single and
 * multi choice alike: every row stays in the tab order, so no radio-group arrow-key contract is promised that
 * is not kept (TP uses radio/checkbox roles; we deliberately do not). Selection reads from the filled surface
 * and the check, not colour alone.
 */
export function OptionRow({ label, hint, icon: Icon, selected, disabled, onClick, ariaLabel }: OptionRowProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      title={label}
      className={cn(
        'group/option flex min-h-9 min-w-0 items-center gap-2.5 border border-ds-hairline px-2 py-1.5 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus',
        'disabled:cursor-not-allowed disabled:border-ds-disabled-border disabled:bg-ds-disabled-bg disabled:text-ds-disabled-text',
        selectionSurface({ level: 'card-tab', selected }),
      )}
    >
      {Icon ? (
        <span
          aria-hidden="true"
          className="flex size-6 shrink-0 items-center justify-center rounded-sm bg-ds-ink-a05 text-ds-text-2 group-disabled/option:text-ds-disabled-text"
        >
          <Icon className="size-3.5" />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body-sm text-foreground group-disabled/option:text-ds-disabled-text">{label}</span>
        {hint ? (
          <span className="block truncate text-label text-ds-text-3 group-disabled/option:text-ds-disabled-text">{hint}</span>
        ) : null}
      </span>
      {selected ? <Check aria-hidden="true" className="size-4 shrink-0" /> : null}
    </button>
  );
}
